import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

import ReactEcharts from "echarts-for-react";
import { format, addDays, addMonths, isSameMonth } from "date-fns";

import axios from "@app/lib/axios";

import CommonHorizontalBarChart from "@app/components/CommonHorizontalBarChart";
import CommonVerticalBarChart from "@app/components/CommonVerticalBarChart";

import style from "../Components/style";

import NurseryClassUse from "./NurseryClassUse";

const Class = forwardRef(({ statPeriod, selectedBranch, selectedNurseryUniq, selectedClass, stats }, ref) => {
    const [statContent, setStatContent] = useState(false);
    const [statMenu, setStatMenu] = useState(false);

    const initStat = () => {
        setStatContent(false);
        setStatMenu(false);
    };

    useImperativeHandle(ref, () => ({
        getExcelData(callback) {
            const excelData = {
                fileName: `토모노트_${selectedNurseryUniq.nurseryUniqName}_${selectedClass.className}`,
                sheets: [
                    {
                        name: "반",
                        title: `${selectedNurseryUniq.nurseryUniqName} - ${selectedClass.className} - 콘텐츠 사용량`,
                        columnTitle: ["일자", "사용량"],
                        data: statContent ? statContent.map((s) => [s.statDate, s.playCountSum || 0]) : [],
                    },
                    {
                        name: "메뉴",
                        title: `${selectedNurseryUniq.nurseryUniqName} - ${selectedClass.className} - 메뉴별 사용량`,
                        columnTitle: ["메뉴", "사용량"],
                        data: statMenu ? statMenu.map((s) => [s.menu, s.playCountSum || 0]) : [],
                    },
                    {
                        name: "사용현황",
                        title: `${selectedNurseryUniq.nurseryUniqName} - ${selectedClass.className} - 사용 현황`,
                        columnTitle: ["반", "가입일", "최종 접속일", "앱 버전"],
                        data: selectedClass ? [selectedClass].map((c) => [c.className, c.createDate, c.lastAccess, c.version]) : [],
                    },
                ],
            };
            callback(null, excelData);
        },
    }));
    const applyStat = () => {
        initStat();
        if (stats) {
            let statContent = [];
            let statMenu = [];

            let start = new Date(statPeriod.startDate);
            let day = new Date(statPeriod.startDate);
            for (let i = 0; i < 99; i++) {
                const statDate = format(day, statPeriod.type === "year" ? "yyyy-MM" : "yyyy-MM-dd");
                const statDateStr = format(day, statPeriod.type === "year" ? "yyyy-MM" : "MM-dd");
                const sContent = stats.filter((s) => s.statDate === statDate);
                const s = { statDate: statDateStr, playCount: 0, playCountSum: 0 };
                if (sContent) {
                    s.playCount = sContent.reduce((acc, c) => acc + c.playCount, 0);
                    s.playCountSum = sContent.reduce((acc, c) => acc + c.playCountSum, 0);
                }
                statContent.push(s);

                if (statPeriod.type === "week") {
                    day = addDays(day, 1);
                    if (i >= 6) break;
                } else if (statPeriod.type === "month") {
                    day = addDays(day, 1);
                    if (!isSameMonth(day, start)) break;
                } else {
                    // year
                    day = addMonths(day, 1);
                    if (i > 11) break;
                }
            }

            stats.forEach((s) => {
                const m = statMenu.find((m) => m.menu == s.menu);
                let playCount = 0;
                let playCountSum = 0;
                if (m) {
                    m.playCount += s.playCount || 0;
                    m.playCountSum += s.playCountSum || 0;
                } else {
                    statMenu.push({ menu: s.menu, playCount: s.playCount || 0, playCountSum: s.playCountSum || 0 });
                }
            });

            setStatContent(statContent);
            setStatMenu(statMenu);
        }
    };
    useEffect(() => {
        applyStat();
        return () => {};
    }, [selectedClass, stats]);

    const heightChartMenu = statMenu && statMenu.length > 10 ? statMenu.length * 20 + 25 : 250;
    const optionContent = {
        xAxis: { field: "statDate" },
        yAxis: {
            name: "건",
            series: style.seriesBranchClassPlayCount,
        },
        stacks: [{ stack: "base", showSum: false, showPercent: false }],
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "cross",
                crossStyle: {
                    color: "#999",
                },
            },
        },
        showLegend: false,
        grid: {
            top: "15%",
            left: "5%",
            right: "5%",
            bottom: "10%",
        },
    };
    const menuChartOption = {
        xAxis: {
            name: "건",
            series: style.seriesBranchClassMenuPlayCount,
        },
        yAxis: { field: "menu" },
        stacks: [{ stack: "base", showPercent: false, showSum: false, showSumTooltip: false }],
        showLegend: false,
        sort: false,
        grid: {
            top: "5%",
            left: "10%",
            right: "5%",
            bottom: "10%",
        },
    };
    return (
        <div className="w-100">
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">콘텐츠 사용량</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statContent} chartOption={optionContent} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">메뉴별 사용량</h4>
                        </div>
                        <div className="card-body" style={{ height: `${heightChartMenu}px` }}>
                            <CommonVerticalBarChart chartData={statMenu} chartOption={menuChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">사용 현황</h4>
                        </div>
                        <div className="card-body">
                            <NurseryClassUse classList={[selectedClass]} isClassView={true} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Class;
